import {
    IAPIBOPISInventory,
    IBOPISStore,
    IRaw,
    ISkusOnhand,
    IDoorStatus
} from './BOPISApiInterfaces';
import { IStore, UnitDistance, IOpening, IOpeningRaw, IActiveFilters } from './Interfaces';
import { ACTIVE } from '../constants/constants';
import { formatBopisTime, getDayLabel } from 'utils/formatters';

interface IBopisDoorFilteringInfo {
    productIsAvailable: boolean;
    storeHoursRaw: { [key: string]: IRaw };
}

const filtersMap: { [filter: string]: (door: IBopisDoorFilteringInfo) => boolean } = {
    openNow: function (door: IBopisDoorFilteringInfo) {
        if (!door.storeHoursRaw || !Object.keys(door.storeHoursRaw).length) {
            return false;
        }
        const date = new Date(Date.now());
        const currDay = date.getDay();
        const currTime = parseInt(`${date.getHours()}${date.getMinutes()}`, 10);
        const doorClose = parseInt(door.storeHoursRaw[currDay - 1].close, 10);
        const doorOpen = parseInt(door.storeHoursRaw[currDay - 1].open, 10);

        return currTime < doorClose && currTime > doorOpen;
    },
    storesWithAvailability: function (door: IBopisDoorFilteringInfo) {
        return door.productIsAvailable;
    }
};

function isFiltersMatched(filters: string[], door: IBopisDoorFilteringInfo): boolean {
    if (!filters.length) {
        return true;
    }

    let isFiltersMatch = true;

    filters.forEach((filter) => {
        const isMatched = filtersMap[filter](door);
        if (!isMatched) {
            isFiltersMatch = false;
        }
    });

    return isFiltersMatch;
}

const APIBOPISDoorToAPIStore: (
    bopisDoor: IBOPISStore,
    countryCode: string,
    productIsAvailable: boolean,
    storeHours: IOpening[],
    cartItems: { [key: string]: ISkusOnhand },
    skusOnhand: { [key: string]: ISkusOnhand },
    rawStoreHours: IOpeningRaw,
    omni_door_status: IDoorStatus
) => IStore = (
    {
        CITY,
        COUNTRY,
        ADDRESS,
        ADDRESS2,
        ZIP_OR_POSTAL,
        STATE_OR_PROVINCE,
        LONGITUDE,
        LATITUDE,
        DISTANCE,
        DOORNAME,
        DOOR_ID,
        PHONE1,
        HAS_CURBSIDE_PICKUP
    },
    countryCode,
    productIsAvailable,
    storeHours,
    cartItems,
    skusOnhand,
    rawStoreHours,
    omni_door_status
) => ({
    id: DOOR_ID,
    name: DOORNAME,
    address: {
        city: CITY,
        country: COUNTRY,
        country_code: countryCode,
        neighbourhood: `${ADDRESS || ''} ${ADDRESS2 || ''}`,
        postal_code: ZIP_OR_POSTAL,
        state: STATE_OR_PROVINCE,
        description: '' // Irrelevant for BOPIS feature
    },
    curbsideIsAvailable: HAS_CURBSIDE_PICKUP,
    opening: storeHours,
    openingHoursRaw: rawStoreHours,
    geographical: {
        coordinates: {
            lat: Number(LATITUDE),
            lon: Number(LONGITUDE)
        },
        distance: Number(DISTANCE),
        unit: UnitDistance.MILES // Will change in future versions
    },
    phone_number: [
        {
            label: PHONE1 ? 'main' : 'placeholder',
            position: 0,
            value: PHONE1
        }
    ],
    showStoreDetails: false,
    counter_id: '', // Irrelevant for BOPIS feature
    jdaStoreNumber: '', // Irrelevant for BOPIS feature
    productIsAvailable,
    cartItems,
    skusOnhand,
    upcomingEventsNumber: 0,
    courierDelivery: omni_door_status.courier_delivery
});

export const APIBOPISInventoryToAPIStoresArray = (
    apiBopisInventory: IAPIBOPISInventory,
    skuBaseId: number,
    filters: IActiveFilters = {}
) => {
    const activeFilters = Object.keys(filters).filter((f) => filters[f]);

    return apiBopisInventory.doors
        ? Object.keys(apiBopisInventory.doors).reduce<IStore[]>((activeStores, doorId) => {
              if (apiBopisInventory.doors[doorId].omni_door_status.bopis === ACTIVE) {
                  const productOnHandSkus =
                      apiBopisInventory.doors[doorId].door_inventory.skus_onhand[
                          skuBaseId.toString()
                      ];
                  const isProductAvailable = Boolean(
                      productOnHandSkus && productOnHandSkus.is_available
                  );
                  const rawStoreHours =
                      apiBopisInventory.doors[doorId].store_hours.opening_hours.raw;
                  const storeHours = Object.values(rawStoreHours).map((storeHoursItem, index) => {
                      return {
                          day_of_week: index + 1,
                          label: getDayLabel(index),
                          open_hours: [
                              {
                                  close: formatBopisTime(storeHoursItem.close.toString()),
                                  open: formatBopisTime(storeHoursItem.open.toString())
                              }
                          ]
                      };
                  });
                  const cartItems = apiBopisInventory.doors[doorId].door_inventory.cart_skus_onhand;
                  const skusOnhand = apiBopisInventory.doors[doorId].door_inventory.skus_onhand;
                  const doorFilteringInfo: IBopisDoorFilteringInfo = {
                      productIsAvailable: isProductAvailable,
                      storeHoursRaw: rawStoreHours
                  };

                  const isFiltersMatch = isFiltersMatched(activeFilters, doorFilteringInfo);
                  const doorStatus = apiBopisInventory.doors[doorId].omni_door_status;
                  if (isFiltersMatch) {
                      activeStores.push(
                          APIBOPISDoorToAPIStore(
                              apiBopisInventory.doors[doorId].info,
                              apiBopisInventory.country_code,
                              isProductAvailable,
                              storeHours,
                              cartItems,
                              skusOnhand,
                              rawStoreHours,
                              doorStatus
                          )
                      );
                  }
              }

              return activeStores;
          }, [])
        : [];
};
